import { ChangeEvent, useMemo } from "react"
import { useIntegrationSettingsForm, useAppSelector } from "../../../../hooks"
import { selectUserDetails } from "../../../../../redux/slices/auth"
import {
  useFetchLoanOfficerQuery,
  useFetchOrgDetailsQuery
} from "../../../../../redux/queries"
import { getUserBrandId } from "../../../../utils"
import CalcCheckboxes from "./calc-checkboxes"
import LeadWorkflowOption from "./lead-workflow"
import ContactForm from "./contact-form"
import { Integration } from "../../../../../types"
import RateCardsOption from "./rate-cards-option"

interface Props {
  integration?: Integration
}

/**
 * TODO: We should standardize the way we save widget settings
 */
const WidgetsCheckboxes = ({ integration }: Props) => {
  const { app_settings, saveSetting, values } =
    useIntegrationSettingsForm(integration)

  const { settings } = app_settings ?? {}
  const user = useAppSelector(selectUserDetails)

  // Determine owner: use values.owner if set, otherwise current user
  const ownerId = values.owner || user?.id || ""
  const isCurrentUser = ownerId === user?.id

  // Only fetch owner data when:
  // 1. integration.brand_id is missing (need fallback)
  // 2. owner is not the current user (current user org already available)
  const needsOwnerFetch = !integration?.brand_id && !isCurrentUser && !!ownerId

  const { data: ownerData } = useFetchLoanOfficerQuery(ownerId, {
    skip: !needsOwnerFetch
  })

  // Fetch owner's org details to get brands
  const { data: ownerOrg } = useFetchOrgDetailsQuery(
    { id: String(ownerData?.org_id ?? "") },
    { skip: !needsOwnerFetch || !ownerData?.org_id }
  )

  const onChangeSettings = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name
    saveSetting({ [name]: !settings?.[name as keyof typeof settings] })
  }

  const brandId = useMemo(() => {
    // Prefer existing brand_id from integration or form values
    if (integration?.brand_id) return integration.brand_id
    if (values.brand_id) return values.brand_id

    // Determine which org to use for brand lookup
    const orgToUse = isCurrentUser ? user?.org : ownerOrg

    // Use getUserBrandId to find the brand where this user is the default LO
    if (orgToUse && ownerId) {
      const fallbackBrandId = getUserBrandId(orgToUse, ownerId)
      return fallbackBrandId ?? orgToUse?.brands?.[0]?.id
    }

    return undefined
  }, [
    integration?.brand_id,
    values.brand_id,
    isCurrentUser,
    user?.org,
    ownerOrg,
    ownerId
  ])

  // Create an integration object with the resolved brandId (fallback to form values when integration is undefined)
  const integrationWithBrandId = useMemo(() => {
    const base = integration ?? values
    if (!base) return undefined
    return { ...base, brand_id: brandId ?? base.brand_id ?? "" }
  }, [integration, values, brandId])

  return (
    <fieldset>
      <legend className="pt-6 font-medium">Widgets</legend>
      <p className="text-neutral-400 pb-3">
        Choose the embeds you would like to integrate
      </p>
      <CalcCheckboxes />
      <LeadWorkflowOption integration={integrationWithBrandId} />
      <ContactForm integration={integrationWithBrandId} />
      <RateCardsOption integration={integrationWithBrandId} />
      <div className="flex items-center py-2">
        <input
          checked={settings?.loReviews ?? false}
          onChange={onChangeSettings}
          id="loReviews"
          name="loReviews"
          type="checkbox"
          className="h-6 w-6 rounded border-neural-400 text-primary-500 focus:ring-primary-300 cursor-pointer"
        />
        <label htmlFor="loReviews" className="select-none ml-3 cursor-pointer">
          LO Reviews
        </label>
      </div>
      <div className="flex items-center py-2">
        <input
          checked={settings?.brandReviews ?? false}
          onChange={onChangeSettings}
          id="brandReviews"
          name="brandReviews"
          type="checkbox"
          className="h-6 w-6 rounded border-neural-400 text-primary-500 focus:ring-primary-300 cursor-pointer"
        />
        <label
          htmlFor="brandReviews"
          className="select-none ml-3 cursor-pointer"
        >
          Brand Reviews
        </label>
      </div>
      <div className="flex items-center py-2">
        <input
          checked={settings?.aboutBoxes ?? false}
          onChange={onChangeSettings}
          id="aboutBoxes"
          name="aboutBoxes"
          type="checkbox"
          className="h-6 w-6 rounded border-neural-400 text-primary-500 focus:ring-primary-300 cursor-pointer"
        />
        <label htmlFor="aboutBoxes" className="select-none ml-3 cursor-pointer">
          About Boxes
        </label>
      </div>
    </fieldset>
  )
}

export default WidgetsCheckboxes
